import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useRugianoProjectApi from "hooks/api/private/useRugianoProjectApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";

import { DesktopMobileForm, MetaFields } from "types/api/commons";
import {
    RugianoProjectBody,
    RugianoProjectDetails,
    RugianoProjectTypeEnum,
} from "types/api/rugianoProject";
import { DesktopOrMobile } from "types/enums";

import { emptyMetaFields, metaFieldsSchema } from "utils/metaFields";
import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { notEmptyMedia } from "utils/validators";

export type RugianoProjectBodyForm = {
    type: RugianoProjectTypeEnum;
    graphical_order: number | null;
    name: string;
    slug: string;
    enabled: boolean;
    thumb: DesktopMobileForm;
    designer: string | null;
    location: string | null;
    year: number | null;
    text: MetaFields;
};

export type RugianoProjectFormHook = {
    setDesktopMobileImageThumbnail: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    formHook: FormHookType<RugianoProjectBodyForm>;
};

function useRugianoProjectForm({ projectId }: { projectId?: string }): RugianoProjectFormHook {
    const { createRugianoProject, updateRugianoProject, getOneRugianoProject } =
        useRugianoProjectApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                type: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                graphical_order: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                name: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                slug: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                thumb: notEmptyMedia,
                text: metaFieldsSchema,
            },
        };
    }, []);

    const initialValues: RugianoProjectBodyForm = React.useMemo(() => {
        return {
            type: RugianoProjectTypeEnum.CONTRACT,
            graphical_order: null,
            name: "",
            slug: "",
            designer: "",
            location: "",
            year: null,
            enabled: false,
            thumb: {
                id: undefined,
                desktop: createAttachmentType({ label: "Desktop" }),
                mobile: createAttachmentType({ label: "Mobile" }),
            },
            text: emptyMetaFields(),
        };
    }, []);

    const formHook = useForm<RugianoProjectBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [projectDetails, setProjectDetails] = React.useState<RugianoProjectDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (projectId) {
            const [apiCall, abortController] = getOneRugianoProject({ id: projectId });

            apiCall.then(setProjectDetails);

            return () => abortController.abort();
        }
    }, [getOneRugianoProject, projectId]);

    React.useEffect(() => {
        if (projectDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.type = projectDetails.type;
                newForm.graphical_order = projectDetails.graphical_order;
                newForm.name = projectDetails.name;
                newForm.slug = projectDetails.slug;
                newForm.enabled = projectDetails.enabled;
                newForm.designer = projectDetails.designer;
                newForm.location = projectDetails.location;
                newForm.year = projectDetails.year;

                newForm.text.it = {
                    meta_title: projectDetails.text?.it?.meta_title ?? "",
                    meta_description: projectDetails.text?.it?.meta_description ?? "",
                    meta_keywords: projectDetails.text?.it?.meta_keywords ?? "",
                };

                newForm.text.en = {
                    meta_title: projectDetails.text?.en?.meta_title ?? "",
                    meta_description: projectDetails.text?.en?.meta_description ?? "",
                    meta_keywords: projectDetails.text?.en?.meta_keywords ?? "",
                };

                if (projectDetails.thumb) {
                    newForm.thumb = {
                        id: projectDetails.thumb.id,
                        desktop: createAttachmentType({
                            id: projectDetails.thumb.desktop.id ?? null,
                            label: "Desktop",
                            filename: projectDetails.thumb.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: projectDetails.thumb.mobile.id ?? null,
                            label: "Mobile",
                            filename: projectDetails.thumb.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, projectDetails]);

    const rugianoProjectBody: RugianoProjectBody = React.useMemo(() => {
        const body: RugianoProjectBody = {
            type: form.type,
            graphical_order: form.graphical_order ?? 0,
            name: form.name,
            slug: form.slug,
            enabled: form.enabled,
            designer: form.designer,
            location: form.location,
            year: form.year,
            thumb: {
                id: form.thumb.id,
                desktop: converterAttachmentToFileWithPayload(form.thumb.desktop),
                mobile: converterAttachmentToFileWithPayload(form.thumb.mobile),
            },
            text: {
                it: { ...form.text.it },
                en: { ...form.text.en },
            },
        };

        return body;
    }, [form]);

    React.useEffect(() => {
        addSaveAction("useRugianoProjectForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                if (projectId) {
                    const [apiCall] = updateRugianoProject({ id: projectId }, rugianoProjectBody);

                    promises.push(apiCall);
                } else {
                    const [apiCall] = createRugianoProject(rugianoProjectBody);

                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createRugianoProject,
        updateRugianoProject,
        rugianoProjectBody,
        projectId,
        isValid,
    ]);

    const { setDesktopMobileImage: setDesktopMobileImageThumbnail } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "thumb",
    });

    return React.useMemo(
        (): RugianoProjectFormHook => ({
            setDesktopMobileImageThumbnail,
            formHook,
        }),
        [setDesktopMobileImageThumbnail, formHook],
    );
}

export default useRugianoProjectForm;
